import { useEffect, useCallback, useRef } from 'react';

export type ShortcutHandler = () => void | Promise<void>;

export interface ShortcutDefinition {
  key: string;
  ctrlOrCmd?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  category: string;
  handler: ShortcutHandler;
  enabled?: boolean;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: ShortcutDefinition[];
  enabled?: boolean;
}

/**
 * Detects if the user is on a Mac platform
 */
export const isMac = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform) || /Mac/.test(navigator.userAgent);
};

/**
 * Formats a keyboard shortcut for display
 */
export const formatShortcut = (shortcut: ShortcutDefinition): string => {
  const parts: string[] = [];
  const mac = isMac();

  if (shortcut.ctrlOrCmd) {
    parts.push(mac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shift) {
    parts.push(mac ? '⇧' : 'Shift');
  }
  if (shortcut.alt) {
    parts.push(mac ? '⌥' : 'Alt');
  }

  // Format the key
  const key = shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key;
  parts.push(key);

  return parts.join(mac ? '' : '+');
};

/**
 * Hook to register and handle keyboard shortcuts
 */
export const useKeyboardShortcuts = ({ shortcuts, enabled = true }: UseKeyboardShortcutsOptions) => {
  const shortcutsRef = useRef<ShortcutDefinition[]>(shortcuts);

  // Update ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in input fields (except for specific cases)
    const target = event.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' || 
                        target.tagName === 'TEXTAREA' || 
                        target.isContentEditable;

    for (const shortcut of shortcutsRef.current) {
      // Skip disabled shortcuts
      if (shortcut.enabled === false) continue;

      // Check if the key matches
      const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
      if (!keyMatches) continue;

      // Check modifiers
      const ctrlOrCmdPressed = isMac() ? event.metaKey : event.ctrlKey;
      const ctrlOrCmdMatches = shortcut.ctrlOrCmd ? ctrlOrCmdPressed : !ctrlOrCmdPressed;
      const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;
      const altMatches = shortcut.alt ? event.altKey : !event.altKey;

      if (!ctrlOrCmdMatches || !shiftMatches || !altMatches) continue;

      // Special handling for certain shortcuts
      // Allow Escape to work in input fields
      if (shortcut.key === 'Escape') {
        event.preventDefault();
        void shortcut.handler();
        return;
      }

      // Allow Ctrl/Cmd+K to work in input fields (focus color input)
      if (shortcut.key === 'k' && shortcut.ctrlOrCmd) {
        event.preventDefault();
        void shortcut.handler();
        return;
      }

      // Allow Ctrl/Cmd+/ to work anywhere (help modal)
      if (shortcut.key === '/' && shortcut.ctrlOrCmd) {
        event.preventDefault();
        void shortcut.handler();
        return;
      }

      // For other shortcuts, skip if in input field
      if (isInputField) continue;

      // Prevent default browser behavior for matched shortcuts
      event.preventDefault();
      void shortcut.handler();
      return;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  return {
    formatShortcut,
    isMac: isMac(),
  };
};
